import { useNavigate } from "@tanstack/react-router";
import { useTranslate } from "@tolgee/react";
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from "lucide-react";
import { useState } from "react";
import { LocalizedLink } from "#/components/locale/localized-link";
import { FilterChip } from "#/components/ui/filter-chip";
import { InfoTooltip } from "#/components/ui/info-tooltip";
import { PriceFlash } from "#/features/trade/components/market-info/price-flash";
import type { TradePerpMarketSummary } from "#/features/trade/hooks/use-trade-market";
import { getApiCoin } from "#/features/trade/lib/hyperliquid";
import {
	getLocalizedMarketName,
	getMarketLogoAlt,
} from "#/features/trade/lib/market-display";
import { analytics } from "#/lib/analytics";
import {
	formatMultiple,
	formatPrice,
	formatSignedPercent,
} from "#/lib/formatters";
import { cn } from "#/lib/utils";
import {
	BAGGER_LEVERAGES,
	BAGGER_TIERS,
	type BaggerHighlights,
	type BaggerRow,
	type BaggerSortKey,
	useBaggers,
} from "../hooks/use-baggers";
import type { BaggerWindowKey } from "../lib/baggers-math";

const WINDOW_KEYS: readonly BaggerWindowKey[] = ["30d", "90d", "52w"];

function formatCompactUsd(value: number) {
	return new Intl.NumberFormat("en-US", {
		notation: "compact",
		maximumFractionDigits: value >= 1_000_000_000 ? 2 : 1,
	}).format(value);
}

export function BaggersLayout() {
	const { t } = useTranslate();
	const navigate = useNavigate();
	const {
		rows,
		highlights,
		windowKey,
		changeWindow,
		tier,
		changeTier,
		leverage,
		changeLeverage,
		sort,
		toggleSort,
		isLoadingMarkets,
		isLoadingSeries,
	} = useBaggers();

	const selectMarket = (market: TradePerpMarketSummary) => {
		analytics.track("market_selected", {
			symbol: market.routeSymbol,
			source: "baggers",
			dex: market.dex === "xyz" ? "xyz" : "main",
		});
		void navigate({
			to: "/trade/$symbol",
			params: { symbol: market.routeSymbol },
		});
	};

	return (
		<div className="mx-auto min-h-[70vh] max-w-7xl p-4 pb-16">
			<header className="mt-6 mb-6">
				<h1 className="text-display4 text-foreground sm:text-display3">
					{t("baggers.title-pre")}
					<span className="text-primary">{t("baggers.title-em")}</span>
					{t("baggers.title-post")}
				</h1>
				<p className="mt-1 text-body1 text-muted-foreground">
					{t("baggers.subtitle")}
				</p>
			</header>

			<HighlightCards
				highlights={highlights}
				isLoading={isLoadingSeries}
				windowLabel={t(`baggers.window.${windowKey}`)}
				onSelect={selectMarket}
			/>

			<div className="mt-6 mb-4 flex flex-wrap items-center justify-start gap-2 md:gap-0 md:divide-x md:divide-border">
				<div className="flex items-center gap-1 md:pr-2">
					<span className="mr-1 text-label2-medium text-muted-foreground">
						{t("baggers.filter.window")}
					</span>
					{WINDOW_KEYS.map((key) => (
						<FilterChip
							key={key}
							active={windowKey === key}
							onClick={() => changeWindow(key)}
						>
							{t(`baggers.window.${key}`)}
						</FilterChip>
					))}
				</div>
				<div className="flex items-center gap-1 md:px-2">
					<span className="mr-1 text-label2-medium text-muted-foreground">
						{t("baggers.filter.tier")}
					</span>
					{BAGGER_TIERS.map((value) => (
						<FilterChip
							key={value}
							active={tier === value}
							onClick={() => changeTier(value)}
						>
							{value === 0 ? t("baggers.tier.all") : `${value}x+`}
						</FilterChip>
					))}
				</div>
				<div className="flex items-center gap-1 md:pl-2">
					<InfoTooltip
						content={t("baggers.filter.leverage-tooltip")}
						className="mr-1 text-label2-medium text-muted-foreground"
					>
						{t("baggers.filter.leverage")}
					</InfoTooltip>
					{BAGGER_LEVERAGES.map((value) => (
						<FilterChip
							key={value}
							active={leverage === value}
							onClick={() => changeLeverage(value)}
						>
							{value === 1 ? t("baggers.leverage.spot") : `${value}x`}
						</FilterChip>
					))}
				</div>
			</div>

			<div className="overflow-x-auto">
				<table className="w-full border-collapse text-left">
					<thead>
						<tr className="border-b border-border text-label1-medium text-muted-foreground">
							<th className="w-10 whitespace-nowrap py-3 pl-3 text-right font-normal sm:pl-4">
								#
							</th>
							<th className="w-full max-w-0 whitespace-nowrap px-3 py-3 font-normal sm:px-4">
								{t("baggers.columns.market")}
							</th>
							<th className="whitespace-nowrap px-3 py-3 text-right font-normal sm:px-4">
								{t("baggers.columns.price")}
							</th>
							<SortableHeader
								label={
									leverage === 1
										? t("baggers.columns.multiple")
										: `${t("baggers.columns.levered-multiple")} @${leverage}x`
								}
								sortKey="trailing"
								sort={sort}
								onToggle={toggleSort}
							/>
							<SortableHeader
								label={t("baggers.columns.from-low")}
								sortKey="fromLow"
								sort={sort}
								onToggle={toggleSort}
								className="hidden sm:table-cell"
							/>
							<th className="hidden whitespace-nowrap px-4 py-3 text-right font-normal lg:table-cell">
								{t("baggers.columns.volume")}
							</th>
							<th className="hidden whitespace-nowrap px-4 py-3 text-right font-normal md:table-cell">
								{t("baggers.columns.trend")}
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<BaggerTableRow
								key={row.market.routeSymbol}
								row={row}
								rank={index + 1}
								isLoadingSeries={isLoadingSeries}
								sinceListingLabel={t("baggers.since-listing")}
								onSelect={selectMarket}
							/>
						))}
						{rows.length === 0 && !isLoadingMarkets && (
							<tr>
								<td
									colSpan={7}
									className="px-4 py-12 text-center text-body1 text-muted-foreground"
								>
									{t("baggers.empty")}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<footer className="mt-4 flex flex-col items-end gap-1 text-right text-label2-medium text-muted-foreground">
				<p>{t("baggers.disclaimer")}</p>
				<LocalizedLink
					to="/learn/$slug"
					params={{ slug: "how-to-find-tenbaggers" }}
					className="w-fit text-body2 text-foreground hover:underline"
				>
					{t("baggers.guide-link")} →
				</LocalizedLink>
			</footer>
		</div>
	);
}

type HighlightItem = {
	market: TradePerpMarketSummary;
	price: string;
	metric: string;
	metricClass: string;
};

function HighlightCards({
	highlights,
	isLoading,
	windowLabel,
	onSelect,
}: {
	highlights: BaggerHighlights;
	isLoading: boolean;
	windowLabel: string;
	onSelect: (market: TradePerpMarketSummary) => void;
}) {
	const { t } = useTranslate();

	const multipleItem = (row: BaggerRow, value: number): HighlightItem => ({
		market: row.market,
		price: formatPrice(row.market.markPrice),
		metric: formatMultiple(value),
		metricClass: value >= 1 ? "text-trade-green" : "text-trade-red",
	});

	const cards: { title: string; items: HighlightItem[] }[] = [
		{
			title: `${t("baggers.kpi.top")} · ${windowLabel}`,
			items: highlights.trailing.map((row) =>
				multipleItem(row, row.stats?.trailing ?? 0),
			),
		},
		{
			title: t("baggers.columns.from-low"),
			items: highlights.fromLow.map((row) => ({
				...multipleItem(row, row.stats?.fromLow ?? 0),
				metricClass: "text-foreground",
			})),
		},
		{
			title: t("baggers.cards.momentum-30d"),
			items: highlights.momentum.map(({ market, trailing }) => ({
				market,
				price: formatPrice(market.markPrice),
				metric: formatMultiple(trailing),
				metricClass: trailing >= 1 ? "text-trade-green" : "text-trade-red",
			})),
		},
		{
			title: t("baggers.columns.volume"),
			items: highlights.volume.map((row) => ({
				market: row.market,
				price: formatPrice(row.market.markPrice),
				metric: formatSignedPercent(row.market.priceChangePercent),
				metricClass:
					row.market.priceChangePercent >= 0
						? "text-trade-green"
						: "text-trade-red",
			})),
		},
	];

	return (
		<div className="flex divide-x divide-border overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:gap-6 sm:divide-x-0 sm:overflow-visible sm:pb-0 lg:grid-cols-4 lg:gap-0 lg:divide-x">
			{cards.map((card) => (
				<div
					key={card.title}
					className="min-w-[15rem] flex-1 shrink-0 px-4 first:pl-0 sm:min-w-0 sm:px-0 lg:px-6"
				>
					<p className="mb-3 text-label1-bold text-foreground">{card.title}</p>
					<div className="flex flex-col gap-1">
						{card.items.map((item) => (
							<button
								key={item.market.routeSymbol}
								type="button"
								onClick={() => onSelect(item.market)}
								className="-mx-2 flex w-[calc(100%+1rem)] cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-muted/50"
							>
								<CoinIcon
									coin={item.market.coin}
									dex={item.market.dex}
									size={24}
								/>
								<span className="min-w-0 flex-1 truncate text-label1-bold text-foreground">
									{item.market.coin}
								</span>
								<span className="whitespace-nowrap text-label1-tabular-medium text-muted-foreground">
									<PriceFlash
										value={item.market.markPrice}
										display={item.price}
										resetKey={item.market.routeSymbol}
									/>
								</span>
								<span
									className={cn(
										"min-w-[3.25rem] whitespace-nowrap text-right text-label1-tabular-bold",
										item.metricClass,
									)}
								>
									{item.metric}
								</span>
							</button>
						))}
						{card.items.length === 0 &&
							(["a", "b", "c"] as const).map((slot) => (
								<div key={slot} className="flex items-center gap-2">
									<span
										className={cn(
											"h-6 w-6 rounded-full bg-muted",
											isLoading && "animate-pulse",
										)}
									/>
									<span
										className={cn(
											"h-3 flex-1 rounded bg-muted",
											isLoading && "animate-pulse",
										)}
									/>
								</div>
							))}
					</div>
				</div>
			))}
		</div>
	);
}

// ponytail: hotlinks Hyperliquid's own app icon CDN (serves main coins as
// BTC.svg and xyz markets as xyz:NVDA.svg — exactly our getApiCoin format).
// Unknown coins return the SPA html, the <img> decode fails, and the monogram
// fallback takes over. Vendor the icons locally if HL ever blocks hotlinks.
function coinIconUrl(apiCoin: string): string {
	return `https://app.hyperliquid.xyz/coins/${encodeURIComponent(apiCoin)}.svg`;
}

function CoinIcon({
	coin,
	dex,
	size,
}: {
	coin: string;
	dex?: string;
	size: number;
}) {
	const { t } = useTranslate();
	const [failed, setFailed] = useState(false);
	if (failed) {
		return (
			<span
				style={{ width: size, height: size }}
				className="flex shrink-0 items-center justify-center rounded-full bg-muted text-label2-bold text-muted-foreground"
			>
				{coin.slice(0, 1)}
			</span>
		);
	}
	return (
		<img
			src={coinIconUrl(getApiCoin(coin))}
			alt={getMarketLogoAlt({ coin, dex, t })}
			width={size}
			height={size}
			loading="lazy"
			onError={() => setFailed(true)}
			className="shrink-0 rounded-full bg-muted"
		/>
	);
}

function SortableHeader({
	label,
	sortKey,
	sort,
	onToggle,
	className,
}: {
	label: string;
	sortKey: BaggerSortKey;
	sort: { key: BaggerSortKey; desc: boolean };
	onToggle: (key: BaggerSortKey) => void;
	className?: string;
}) {
	const isActive = sort.key === sortKey;
	return (
		<th
			className={cn(
				"whitespace-nowrap px-3 py-3 text-right font-normal sm:px-4",
				className,
			)}
		>
			<button
				type="button"
				onClick={() => onToggle(sortKey)}
				className={cn(
					"inline-flex cursor-pointer items-center gap-1 hover:text-foreground",
					isActive && "text-foreground",
				)}
			>
				{label}
				{isActive ? (
					sort.desc ? (
						<ArrowDownIcon className="size-3" />
					) : (
						<ArrowUpIcon className="size-3" />
					)
				) : (
					<ChevronsUpDownIcon className="size-3 opacity-40" />
				)}
			</button>
		</th>
	);
}

function BaggerTableRow({
	row,
	rank,
	isLoadingSeries,
	sinceListingLabel,
	onSelect,
}: {
	row: BaggerRow;
	rank: number;
	isLoadingSeries: boolean;
	sinceListingLabel: string;
	onSelect: (market: TradePerpMarketSummary) => void;
}) {
	const { t } = useTranslate();
	const { market, stats, trend, displayMultiple, liquidated } = row;

	return (
		<tr
			onClick={() => onSelect(market)}
			className="cursor-pointer border-b border-border last:border-b-0 transition-colors hover:bg-muted/50"
		>
			<td className="whitespace-nowrap py-3 pl-3 text-right text-label1-tabular-medium text-muted-foreground sm:pl-4">
				{rank}
			</td>
			<td className="w-full max-w-0 px-3 py-3 sm:px-4">
				<div className="flex items-center gap-2.5">
					<CoinIcon coin={market.coin} dex={market.dex} size={28} />
					<div className="min-w-0">
						<div className="flex items-start gap-2">
							<div className="truncate text-label1-bold text-foreground">
								<span className="sm:hidden">{market.coin}</span>
								<span className="hidden sm:inline">{market.displaySymbol}</span>
							</div>
							<span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-label2-medium text-muted-foreground">
								{market.maxLeverage}x
							</span>
						</div>
						<div className="truncate text-label2-medium text-muted-foreground">
							{getLocalizedMarketName({
								coin: market.coin,
								dex: market.dex,
								t,
							})}
						</div>
					</div>
				</div>
			</td>
			<td className="whitespace-nowrap px-3 py-3 text-right text-label1-tabular-medium text-foreground sm:px-4">
				<PriceFlash
					value={market.markPrice}
					display={formatPrice(market.markPrice)}
					resetKey={market.routeSymbol}
				/>
			</td>
			<td className="whitespace-nowrap px-3 py-3 text-right sm:px-4">
				<div className="inline-flex flex-col items-center">
					<MultipleCell
						value={displayMultiple}
						liquidated={liquidated}
						liquidatedLabel={t("baggers.liquidated")}
						isLoading={isLoadingSeries}
					/>
					{stats?.clamped && (
						<div className="mt-0.5 text-label2-medium text-muted-foreground">
							{sinceListingLabel}
						</div>
					)}
				</div>
			</td>
			<td className="hidden whitespace-nowrap px-4 py-3 text-right sm:table-cell">
				{stats ? (
					<span className="text-label1-tabular-medium text-foreground">
						{formatMultiple(stats.fromLow)}
					</span>
				) : (
					<MissingCell isLoading={isLoadingSeries} />
				)}
			</td>
			<td className="hidden whitespace-nowrap px-4 py-3 text-right text-label1-tabular-medium text-foreground lg:table-cell">
				${formatCompactUsd(market.dayNotionalVolume)}
			</td>
			<td className="hidden px-4 py-3 md:table-cell">
				<div className="flex justify-end">
					{trend && trend.length > 1 ? (
						<Sparkline points={trend} positive={(stats?.trailing ?? 1) >= 1} />
					) : (
						<MissingCell isLoading={isLoadingSeries} />
					)}
				</div>
			</td>
		</tr>
	);
}

function MultipleCell({
	value,
	liquidated,
	liquidatedLabel,
	isLoading,
}: {
	value: number | null;
	liquidated: boolean;
	liquidatedLabel: string;
	isLoading: boolean;
}) {
	if (value == null) return <MissingCell isLoading={isLoading} />;
	const up = value >= 1;
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 whitespace-nowrap border-b-2 pb-0.5 text-label1-tabular-bold",
				up && !liquidated
					? "border-trade-green/40 text-trade-green"
					: "border-trade-red/40 text-trade-red",
			)}
		>
			{liquidated ? (
				liquidatedLabel
			) : (
				<>
					{up ? (
						<ArrowUpIcon className="size-3" />
					) : (
						<ArrowDownIcon className="size-3" />
					)}
					{formatMultiple(value)}
				</>
			)}
		</span>
	);
}

function MissingCell({ isLoading }: { isLoading: boolean }) {
	return (
		<span
			className={cn(
				"text-label1-medium text-muted-foreground",
				isLoading && "animate-pulse",
			)}
		>
			{isLoading ? "···" : "—"}
		</span>
	);
}

/** Single-series row sparkline — windowed daily closes, no axes by design. */
function Sparkline({
	points,
	positive,
}: {
	points: number[];
	positive: boolean;
}) {
	const width = 88;
	const height = 24;
	const lo = Math.min(...points);
	const hi = Math.max(...points);
	const span = hi - lo || 1;
	const last = points.length - 1;
	const coords = points
		.map(
			(value, i) =>
				`${((i / last) * width).toFixed(1)},${(height - ((value - lo) / span) * height).toFixed(1)}`,
		)
		.join(" ");
	return (
		<svg
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			aria-hidden="true"
			className="overflow-visible"
		>
			<polyline
				points={coords}
				fill="none"
				stroke={positive ? "var(--trade-green)" : "var(--trade-red)"}
				strokeWidth={1.5}
				strokeLinejoin="round"
				strokeLinecap="round"
				vectorEffect="non-scaling-stroke"
			/>
		</svg>
	);
}
